import type { Option } from "../core/option.ts";
import { PrefixedUuid } from "@tento-core/prefixed-uuid";
import { Uuid } from "@tento-core/uuid";
import { WeakIntern } from "./weak-intern.ts";

const HEX32_RE = /^[0-9a-f]{32}$/i;

/**
 * Deterministic 128-bit hash for ephemeral-name interning.
 *
 * Same name always produces the same hex, so
 * `SheetId.ephemeral("foo")` is reference-equal across calls (and
 * across re-parses of the legacy `"ephemeral(foo)"` form) while it is held.
 *
 * Uses 4 parallel 32-bit FNV-1a runs with different leading bytes
 * for diversification — sufficient collision resistance for the
 * bounded set of ephemeral names a session creates.
 */
function deterministicEphemeralHex(name: string): string {
  const input = `ephemeral:${name}`;
  const FNV_OFFSET = 0x811c9dc5;
  const FNV_PRIME = 0x01000193;
  let result = "";
  for (let pass = 0; pass < 4; pass++) {
    let h = FNV_OFFSET >>> 0;
    h ^= pass;
    h = Math.imul(h, FNV_PRIME) >>> 0;
    for (let i = 0; i < input.length; i++) {
      h ^= input.charCodeAt(i);
      h = Math.imul(h, FNV_PRIME) >>> 0;
    }
    result += h.toString(16).padStart(8, "0");
  }
  return result;
}

/**
 * A sheet's id, parsed and interned: a server id or a local (ephemeral or
 * builtin) one. Immutable — its canonical spelling and variant, nothing
 * else. An ephemeral sheet's name is not on its id: it stays on the spec
 * that opened the sheet (`ephemeral(<name>)`, the sheet's title).
 *
 * Interned weakly (`WeakIntern`): every parse of a spelling answers the one
 * instance while anything holds it, so ids compare by identity (`equals`,
 * `===`, `Map` keys), and the table holds no id nobody uses.
 */
export class SheetId {
  static readonly #cache = new WeakIntern<SheetId>();

  readonly #variant: SheetId.Variant;
  readonly #canonical: string;
  private constructor(
    canonical: string,
    variant: SheetId.Variant,
  ) {
    this.#canonical = canonical;
    this.#variant = variant;
    Object.freeze(this);
  }

  static of(raw: string): SheetId {
    const cached = SheetId.#cache.get(raw);
    if (cached) return cached;

    const { canonical, variant } = SheetId.#canonicalize(raw);
    const cachedCanonical = SheetId.#cache.get(canonical);
    if (cachedCanonical) {
      if (raw !== canonical) SheetId.#cache.set(raw, cachedCanonical);
      return cachedCanonical;
    }

    const id = new SheetId(canonical, variant);
    SheetId.#cache.set(canonical, id);
    if (raw !== canonical) SheetId.#cache.set(raw, id);
    return id;
  }

  /**
   * Construct a SheetId for an in-memory ("ephemeral") sheet. The
   * name is hashed to a deterministic UUID so calling with the same
   * name always returns the same interned instance — the one
   * `SheetId.of("ephemeral(<name>)")` answers. The name itself is
   * not kept on the id.
   */
  static ephemeral(name: string): SheetId {
    return SheetId.of(`ss_${deterministicEphemeralHex(name)}`);
  }

  toServerString(): string {
    return this.#canonical;
  }

  toUuid(): Option<Uuid> {
    const hex = this.#variant.hex;
    if (!hex) return null;
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  /**
   * Whether `other` names the same sheet. Ids are interned by canonical form,
   * so this is identity; a caller holding a raw UUID / `ss_` key parses it
   * first (`SheetId.parse(raw)`).
   */
  equals(other: SheetId): boolean {
    return this === other;
  }

  toString(): string {
    return this.#canonical;
  }

  toJSON(): string {
    return this.#canonical;
  }

  static #canonicalize(raw: string): {
    canonical: string;
    variant: SheetId.Variant;
  } {
    // Legacy ephemeral form — canonicalize to deterministic UUID so
    // every site that constructs / parses this form converges to the
    // same interned SheetId.
    if (raw.startsWith("ephemeral(") && raw.endsWith(")")) {
      const name = raw.slice("ephemeral(".length, -1);
      const hex = deterministicEphemeralHex(name);
      return {
        canonical: `ss_${hex}`,
        variant: { tag: "server", hex },
      };
    }

    if (raw.startsWith("ss_")) {
      const hex = raw.slice(3).toLowerCase();
      return {
        canonical: `ss_${hex}`,
        variant: { tag: "server", hex: HEX32_RE.test(hex) ? hex : null },
      };
    }

    if (Uuid.is(raw)) {
      const canonical = PrefixedUuid.serialize("ss", raw).toLowerCase();
      const hex = canonical.slice(3);
      return {
        canonical,
        variant: { tag: "server", hex },
      };
    }

    throw new Error(`Invalid SheetId format: ${raw}`);
  }
}

/** Types and values that go with `SheetId`: `Variant`, `parse`. */
export namespace SheetId {
  /**
   * The variant discriminator now has only one tag — all SheetIds
   * are server-form UUIDs (including ephemerals, which intern to a
   * deterministic UUID). "Local vs synced" persistence lives on
   * `SheetHandle.persistence`, not on the ID itself.
   */
  export type Variant = { tag: "server"; hex: Option<string> };

  /** Try to parse, returning null on invalid format. */
  export function parse(id: string): Option<SheetId> {
    try {
      return SheetId.of(id);
    } catch {
      return null;
    }
  }
}
